import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Heart } from "lucide-react";

const AnnapurnaTemple = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/annapurna0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Annapurna Temple",
    "description": "Sacred temple in Indore dedicated to Goddess Annapurna, the divine provider of food and nourishment",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Sudama Nagar",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-21:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "8500"
    }
  };

  const highlights = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Goddess of Nourishment",
      description: "Sacred temple dedicated to Goddess Annapurna, the divine provider of food and sustenance"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Beautiful Architecture",
      description: "Traditional temple architecture with intricate carvings and peaceful spiritual ambiance"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Community Kitchen",
      description: "Regular prasad distribution and community meals for devotees and visitors"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Spiritual Atmosphere",
      description: "Peaceful environment perfect for meditation, prayers, and spiritual reflection"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "6:00 AM - 9:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 7 AM, Evening 7 PM" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "30 minutes - 1 hour" },
    { label: "Facilities", value: "Parking, Prasad, Community kitchen" }
  ];

  const festivals = [
    { name: "Annapurna Jayanti", time: "Margashirsha Purnima", special: "Grand celebrations with special prayers" },
    { name: "Akshaya Tritiya", time: "April/May", special: "Special offerings and food distribution" },
    { name: "Diwali", time: "October/November", special: "Beautiful decorations and celebrations" },
    { name: "Navratri", time: "March/October", special: "Nine days of devotional activities" }
  ];

  const nearbyAttractions = [
    { name: "Khajrana Ganesh Temple", distance: "2 km", time: "7 mins" },
    { name: "Rajwada Palace", distance: "4 km", time: "12 mins" },
    { name: "Kanch Mandir", distance: "3.5 km", time: "10 mins" },
    { name: "Lal Bagh Palace", distance: "5 km", time: "15 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Annapurna Temple Indore - Divine Nourishment & Blessings | Aaoghumen</title>
        <meta name="description" content="Visit Annapurna Temple in Indore with Aaoghumen - sacred temple dedicated to Goddess Annapurna, divine provider of food. Complete guide with timings, festivals, and spiritual significance." />
        <meta name="keywords" content="annapurna temple indore, annapurna temple timings, indore temples, goddess annapurna, aaoghumen annapurna, annapurna jayanti, indore spiritual places, annapurna mata temple" />
        
        <meta property="og:title" content="Annapurna Temple Indore - Divine Blessings | Aaoghumen" />
        <meta property="og:description" content="Experience divine blessings at Annapurna Temple, dedicated to Goddess Annapurna, the divine provider of food and nourishment in Indore." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/annapurna-temple" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/annapurna-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-yellow-600 via-orange-500 to-red-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Annapurna Temple Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Annapurna Temple</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Seek blessings from Goddess Annapurna, the divine provider of food and nourishment, at this peaceful temple known for its spiritual atmosphere and community service.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.7 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 9 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Annapurna Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Annapurna Temple in Indore is a revered spiritual destination dedicated to Goddess Annapurna, who is worshipped as the divine provider of food and nourishment. The temple holds special significance in Hindu culture as Goddess Annapurna is believed to ensure that no devotee goes hungry and blesses households with abundance and prosperity. This sacred temple attracts thousands of devotees who come seeking blessings for food security, prosperity, and spiritual nourishment.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple features beautiful traditional architecture with intricate carvings and decorations that create a serene and spiritual atmosphere. The peaceful environment makes it an ideal place for meditation, prayer, and spiritual reflection. The temple is known for its community service, regularly organizing food distribution (prasad) and community meals for devotees and those in need, embodying the true spirit of Goddess Annapurna's benevolence.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the spiritual significance of Goddess Annapurna, participate in traditional rituals, and experience the temple's peaceful energy that has made it a beloved spiritual destination in Indore. The temple's commitment to serving the community through food distribution reflects the divine quality of ensuring no one goes hungry.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-yellow-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-yellow-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Special Occasions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-yellow-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Spiritual Experiences */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Spiritual Experiences</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-yellow-600 mb-4">Morning Prayers</h3>
                <p className="text-gray-600 mb-4">Join the peaceful morning prayers at 7 AM and experience the temple's serene spiritual atmosphere.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• 7:00 AM daily</li>
                  <li>• Traditional chants</li>
                  <li>• Peaceful environment</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-yellow-600 mb-4">Prasad Distribution</h3>
                <p className="text-gray-600 mb-4">Participate in the blessed prasad distribution and experience the goddess's divine nourishment.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Daily after aarti</li>
                  <li>• Community meals</li>
                  <li>• Blessed food offerings</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-yellow-600 mb-4">Special Offerings</h3>
                <p className="text-gray-600 mb-4">Make special offerings to Goddess Annapurna for prosperity, abundance, and family well-being.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Food offerings accepted</li>
                  <li>• Prosperity prayers</li>
                  <li>• Family blessings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-yellow-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Sudama Nagar area</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts of city, ₹30-80</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola readily available</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Parking available near temple</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Sudama Nagar, easily accessible</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Annapurna Temple Indore" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Seek Divine Nourishment</h2>
              <p className="text-xl mb-6">Visit Annapurna Temple with spiritual guidance and cultural insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Sudama Nagar, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-yellow-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Visit
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-yellow-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default AnnapurnaTemple;